import React from 'react';
import classnames from 'classnames';
import { PapiUser, PapiEditorText } from '@wix/da-papi-types';
import { useTranslation } from 'react-i18next';

import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Timestamp from '@wix/da-shared-react/pkg/Timestamp';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import GroupAboutViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/GroupAbout';

import { BulletItem } from '@wix/da-shared-react/pkg/Bullets';
import CalendarIcon from '@wix/da-ds/pkg/Icons/Calendar';
import DeviantsIcon from '@wix/da-ds/pkg/Icons/24x24/AllDeviants';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/Image';
import Eye2Icon from '@wix/da-ds/pkg/Icons/Eye2';

import s from './GroupAbout.scss';

export interface Props {
  className?: string;

  // module data
  moduleId: number;
  moduleVersion: number;
  title?: string;
  tagline?: string;
  foundationTs?: number;
  description?: PapiEditorText;

  // redux data
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  deviations: number;
  pageviews: number;
  members: number;
  canJoin: boolean;
  canInvite: boolean;

  // redux actions
  messageAdmins: (group: PapiUser) => void;
  requestToJoin: (group: PapiUser) => void;
  inviteToGroup: (group: PapiUser) => void;
}

const GroupAbout: React.FC<Props> = ({
  className,

  moduleId,
  moduleVersion,
  description,
  foundationTs,

  profileOwnerUser,
  deviations,
  pageviews,
  members,
  canJoin,
  canInvite,

  messageAdmins,
  requestToJoin,
  inviteToGroup,
}) => {
  const { t } = useTranslation();
  return (
    <div className={classnames(s['root'], className)}>
      {description && (
        <div className={s['description']}>
          <TextRenderer
            textContent={description}
            reactViewer={GroupAboutViewer}
            legacyTheme={'default'}
            viewerKey={`group-about-${moduleId}-${moduleVersion}`}
          />
        </div>
      )}
      {foundationTs && (
        <div>
          <BulletItem bullet={<CalendarIcon disableDefaultColors />}>
            Founded:{' '}
            <Timestamp timestamp={new Date(foundationTs).toISOString()} />
          </BulletItem>
        </div>
      )}
      <div className={s['row']}>
        <BulletItem bullet={<DeviantsIcon disableDefaultColors />}>
          {members} Members
        </BulletItem>
        <BulletItem bullet={<ImageIcon disableDefaultColors />}>
          {deviations} Deviations
        </BulletItem>
        <BulletItem bullet={<Eye2Icon disableDefaultColors />}>
          {pageviews} Pageviews
        </BulletItem>
      </div>
      <div className={s['row']}>
        {canInvite && (
          <GhostButton
            size="large"
            variant="main"
            onClick={() => inviteToGroup(profileOwnerUser)}
          >
            {t('widgets.group_about.view.button.invite')}
          </GhostButton>
        )}
        {canJoin && (
          <GhostButton
            size="large"
            variant="main"
            onClick={() => requestToJoin(profileOwnerUser)}
          >
            {t('widgets.group_about.view.button.join')}
          </GhostButton>
        )}
        <GhostButton
          size="large"
          variant="main"
          onClick={() => messageAdmins(profileOwnerUser)}
        >
          {t('widgets.group_about.view.button.messageAdmins')}
        </GhostButton>
      </div>
    </div>
  );
};

GroupAbout.displayName = 'GroupAbout';

export default GroupAbout;
